import { TaskStatus, type TaskCreate } from "@/types/task";
import { validateRequired } from "../helpers/validationHelpers";

export const validateTasks = (values: TaskCreate): Record<string, string> => {
  const errors: Record<string, string> = {};

  // --- Required Top-Level Fields ---
  validateRequired(values.title, "title", errors);
  validateRequired(values.status, "status", errors);

  // --- Business Logic Validations ---

  // Title should be meaningful (not just whitespace)
  if (values.title && values.title.trim().length === 0) {
    errors["title"] = "Title cannot be empty or contain only spaces";
  }

  // Title should not be excessively long
  if (values.title && values.title.length > 255) {
    errors["title"] = "Title cannot exceed 255 characters";
  }

  // Due date should not be in the past (optional but if provided, should be future or today)
  if (values.due_date) {
    const today = new Date().toISOString().split("T")[0]; // Gets YYYY-MM-DD

    if (values.due_date < today) {
      errors["due_date"] = "Due date cannot be in the past";
    }
  }

  // --- Assignment Validation ---

  // Must have either user or role assignment
  if (!values.assigned_user_id_ref && !values.assigned_role_id_ref) {
    errors["assigned_user_id_ref"] = "Task must be assigned to either a user or role";
    errors["assigned_role_id_ref"] = "Task must be assigned to either a user or role";
  }

  // Cannot have both user and role assignment
  if (values.assigned_user_id_ref && values.assigned_role_id_ref) {
    errors["assigned_user_id_ref"] = "Cannot assign to both user and role - choose one";
    errors["assigned_role_id_ref"] = "Cannot assign to both user and role - choose one";
  }

  // --- Related Record Validation ---

  if (values.record) {
    validateRequired(values.record.object_type, "record.object_type", errors);
    validateRequired(values.record.object_id, "record.object_id", errors);

    // Validate object_type is one of allowed values (all 8 types from API)
    const allowedObjectTypes = [
      "stoploss-claims",
      "reimbursement",
      "claimants",
      "clients",
      "policies",
      "endorsements",
      "documents",
    ];
    if (values.record.object_type && !allowedObjectTypes.includes(values.record.object_type)) {
      errors["record.object_type"] = `Object type must be one of: ${allowedObjectTypes.join(", ")}`;
    }

    // Label should be meaningful if provided
    if (values.record.label && values.record.label.trim().length === 0) {
      errors["record.label"] = "Record label cannot be empty if provided";
    }
  }

  // --- Status-Based Validation ---

  // If status is completed, completed flag should be true
  if (values.status === TaskStatus.COMPLETED && !values.completed) {
    errors[TaskStatus.COMPLETED] = "Task must be marked as completed when status is 'completed'";
  }

  // If completed is true, status should be completed
  if (values.completed && values.status !== TaskStatus.COMPLETED) {
    errors["status"] = "Status must be 'completed' when task is marked as completed";
  }

  // --- Description Validation ---

  // Description should not be excessively long
  if (values.description && values.description.length > 2000) {
    errors["description"] = "Description cannot exceed 2000 characters";
  }

  // --- Status Validation ---

  const allowedStatuses = [TaskStatus.OPEN, TaskStatus.IN_PROGRESS, TaskStatus.COMPLETED];
  if (values.status && !allowedStatuses.includes(values.status)) {
    errors["status"] = `Status must be one of: ${allowedStatuses.join(", ")}`;
  }

  return errors;
};
